import { useState, useEffect, useCallback, useMemo } from "react";
import type { TimeSlot, TimeSlotData } from "@/types/subscription";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

interface PublicBooking {
  slot_index: number;
  date_of_booking: string;
  status: string;
}

interface UseTimeSlotsProps {
  date: Date | undefined;
  vendorId: string;
  vendorBusinessDetails?: {
    opens_at: string;
    closes_at: string;
    timeslot_duration: number;
    weekly_holidays: number[];
    holidays: string[];
    unavailable_timeslots: number[];
  };
}

export const useTimeSlots = ({
  date,
  vendorId,
  vendorBusinessDetails
}: UseTimeSlotsProps) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [timeSlotData, setTimeSlotData] = useState<TimeSlotData>({
    startTime: "09:00",
    endTime: "18:00",
    slotDuration: 15,
    weeklyHolidays: [],
    holidays: [],
    unavailableSlots: [],
    bookedSlots: []
  });

  // Utility function to format date as YYYY-MM-DD without timezone issues
  const formatDateForAPI = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Fetch timeslot data for the selected date
  const { data: publicBookings = [], isLoading: isLoadingBookings, isFetching: isFetchingBookings } = useQuery<PublicBooking[]>({
    queryKey: ['publicBookings', vendorId, date?.toISOString()],
    queryFn: async () => {
      if (!vendorId || !date) return [];
      const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/bookings/public/vendor/${vendorId}?date_of_booking=${formatDateForAPI(date)}`);
      return response.data.data;
    },
    enabled: !!vendorId && !!date
  });

  // Function to generate time slots
  const generateTimeSlots = useCallback((slotDuration: number, startTime: string, endTime: string, selectedDate?: Date) => {
    const slots: TimeSlot[] = [];
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const duration = slotDuration;

    const currentTime = new Date(start);
    let index = 0;

    // Check if selected date is today to filter past slots
    const isToday = selectedDate && selectedDate.toDateString() === new Date().toDateString();
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    while (currentTime < end) {
      const slotStartTime = currentTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      currentTime.setMinutes(currentTime.getMinutes() + duration);
      const slotEndTime = currentTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // If it's today, check if the slot is in the past
      if (isToday) {
        const slotHour = new Date(`2000-01-01T${startTime}`);
        slotHour.setMinutes(slotHour.getMinutes() + (index * duration));
        
        // Skip slots that are in the past (with a 15-minute buffer)
        if (slotHour.getHours() < currentHour || (slotHour.getHours() === currentHour && slotHour.getMinutes() < currentMinute + 15)) {
          index++;
          continue;
        }
      }

      slots.push({
        slotIndex: String(index),
        startTime: slotStartTime,
        endTime: slotEndTime,
        slotDuration: slotDuration,
      });

      index++;
    }
    
    return slots;
  }, []);

  // Memoized function to check if a date is a holiday
  const isHoliday = useCallback((checkDate: Date) => {
    // Check weekly holidays (0-6 for Sunday-Saturday)
    const dayOfWeek = checkDate.getDay();
    if (timeSlotData.weeklyHolidays.includes(dayOfWeek)) {
      return true;
    }

    // Check specific holidays - handle object format: { date: "YYYY-MM-DD", name: "Holiday Name", isRecurring: true }
    const dateString = formatDateForAPI(checkDate);
    
    const isSpecificHoliday = timeSlotData.holidays.some((holiday: any) => {
      if (typeof holiday === 'object' && holiday !== null && holiday.date) {
        const holidayDate = holiday.date.includes('T') ? holiday.date.split('T')[0] : holiday.date;
        
        // For recurring holidays, check if the month and day match (ignoring year)
        if (holiday.isRecurring) {
          const holidayDateObj = new Date(holidayDate);
          const checkDateObj = new Date(checkDate);
          
          // Compare month and day (0-indexed)
          return holidayDateObj.getMonth() === checkDateObj.getMonth() && 
                 holidayDateObj.getDate() === checkDateObj.getDate();
        }
        
        // For non-recurring holidays, check exact date match
        return holidayDate === dateString;
      }
      return false;
    });
    
    return isSpecificHoliday;
  }, [timeSlotData.weeklyHolidays, timeSlotData.holidays]);

  // Update time slot data when vendor, date, or publicBookings changes
  useEffect(() => {
    if (vendorId && date && vendorBusinessDetails) {
      const newTimeSlotData = {
        startTime: vendorBusinessDetails.opens_at,
        endTime: vendorBusinessDetails.closes_at,
        slotDuration: vendorBusinessDetails.timeslot_duration,
        weeklyHolidays: vendorBusinessDetails.weekly_holidays || [],
        holidays: vendorBusinessDetails.holidays || [],
        unavailableSlots: vendorBusinessDetails.unavailable_timeslots || [],
        bookedSlots: publicBookings.map((booking: PublicBooking) => booking.slot_index),
      };
      
      setTimeSlotData(newTimeSlotData);
      setTimeSlots(generateTimeSlots(
        newTimeSlotData.slotDuration,
        newTimeSlotData.startTime,
        newTimeSlotData.endTime,
        date
      ));
    }
  }, [date, vendorId, vendorBusinessDetails, publicBookings, generateTimeSlots]);

  // Memoized filtered time slots that excludes unavailable and booked slots for display purposes
  const availableTimeSlots = useMemo(() => {
    return timeSlots.filter(slot => {
      const slotIndex = Number(slot.slotIndex);
      return !timeSlotData.unavailableSlots.includes(slotIndex) && 
             !timeSlotData.bookedSlots.includes(slotIndex);
    });
  }, [timeSlots, timeSlotData.unavailableSlots, timeSlotData.bookedSlots]);

  return {
    timeSlots,
    availableTimeSlots,
    timeSlotData,
    isHoliday,
    generateTimeSlots,
    isLoadingBookings,
    isFetchingBookings
  };
}; 